import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Oee = () => {
  // State variables for OEE calculation
  const [availability, setAvailability] = useState(0); // Availability percentage
  const [performance, setPerformance] = useState(0); // Performance percentage
  const [quality, setQuality] = useState(0); // Quality percentage
  const [oee, setOee] = useState(0); // Calculated OEE

  // Event handlers to update state values
  const handleAvailabilityChange = (e) => setAvailability(parseFloat(e.target.value) || 0);
  const handlePerformanceChange = (e) => setPerformance(parseFloat(e.target.value) || 0);
  const handleQualityChange = (e) => setQuality(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate OEE
  useEffect(() => {
    if (availability > 0 && performance > 0 && quality > 0) {
      const calculatedOee = (availability / 100) * (performance / 100) * (quality / 100) * 100;
      setOee(calculatedOee);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [availability, performance, quality]);

  return (
    <Layout title="Overall Equipment Effectiveness Calculator" path="oee-calculator">
      <section className="section-padding" id="oee-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* OEE Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Overall Equipment Effectiveness Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Availability */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="availability" className="form-label">
                        Availability (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Availability:</strong> The percentage of planned production time the equipment was available for operation."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="availability"
                        placeholder="Enter availability percentage"
                        value={availability || ""}
                        onChange={handleAvailabilityChange}
                      />
                    </div>

                    {/* Input for Performance */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="performance" className="form-label">
                        Performance (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Performance:</strong> The percentage of actual production speed compared to the standard or ideal speed."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="performance"
                        placeholder="Enter performance percentage"
                        value={performance || ""}
                        onChange={handlePerformanceChange}
                      />
                    </div>

                    {/* Input for Quality */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="quality" className="form-label">
                        Quality (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Quality:</strong> The percentage of good units produced compared to the total units produced."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="numOeeber"
                        className="form-control"
                        id="quality"
                        placeholder="Enter quality percentage"
                        value={quality || ""}
                        onChange={handleQualityChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Overall Equipment Effectiveness (OEE):{" "}
                        <span className="fw-bold ms-3">
                          {oee === 0
                            ? "0"
                            : oee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Overall Equipment Effectiveness
                </h3>

                {/* What is OEE? */}
                <h4 className="mt-3 mb-3">What is OEE?</h4>
                <p>
                  **Overall Equipment Effectiveness (OEE)** is a metric that evaluates the efficiency and productivity of production equipment. It is calculated by combining three factors: availability, performance, and quality.
                </p>

                {/* Why Use the OEE Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the OEE Calculator?</h4>
                <p>
                  The OEE Calculator helps measure how effectively a manufacturing operation is utilized. It identifies performance gaps and provides insights for improving efficiency and profitability.
                </p>

                {/* Benefits of Using the OEE Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the OEE Calculator</h4>
                <ul>
                  <li><strong>Improves Production Processes:</strong> Enables businesses to optimize their operations and increase throughput.</li>
                  <li><strong>Identifies Performance Gaps:</strong> Pinpoints areas of inefficiency for targeted improvements.</li>
                  <li><strong>Increases Profitability:</strong> Enhances operational efficiency, leading to cost savings and higher profitability.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the OEE Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Manufacturing Plants:</strong> Helps assess and improve equipment effectiveness.</li>
                  <li><strong>Production Supervisors:</strong> Provides data to monitor and enhance production efficiency.</li>
                  <li><strong>Quality Control Teams:</strong> Assists in maintaining high-quality production standards.</li>
                </ul>

                {/* Question 1 */}
                <h5 className="mt-3">How is OEE calculated?</h5>
                <p>
                  OEE is calculated by multiplying availability, performance, and quality percentages. These factors assess the uptime, speed, and output quality of production equipment.
                </p>

                {/* Question 2 */}
                <h5 className="mt-3">Can OEE be used to compare different machines?</h5>
                <p>
                  Yes, OEE provides a standardized measure to compare the efficiency of different machines or production lines, helping identify areas for improvement.
                </p>

                {/* Question 3 */}
                <h5 className="mt-3">What is considered a good OEE score?</h5>
                <p>
                  An OEE score of 85% is considered world-class. Scores below 60% indicate significant room for improvement in production efficiency.
                </p>

                {/* Question 4 */}
                <h5 className="mt-3">How often should OEE be measured?</h5>
                <p>
                  OEE should be measured regularly, ideally daily or weekly, to monitor trends and implement continuous improvements in production processes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Oee;
